import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  FaSyncAlt,
  FaSearch,
  FaBook,
  FaChevronDown,
  FaChevronRight,
  FaCode,
  FaTimesCircle,
  FaExpand,
  FaCompress,
  FaFileAlt,
  FaFolder,
} from 'react-icons/fa';
import apiService from '../services/apiService';

type TabType = 'files' | 'api';

interface DocFile {
  slug: string;
  filename: string;
  size: number;
  updatedAt: string;
}

const SystemDocs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('files');

  // --- Documentation Files state ---
  const [docFiles, setDocFiles] = useState<DocFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [docContent, setDocContent] = useState<string>('');
  const [loadingContent, setLoadingContent] = useState(false);

  // --- API Docs state (existing) ---
  const [systemDocs, setSystemDocs] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showRawJson, setShowRawJson] = useState(false);

  // Load doc files on mount
  useEffect(() => {
    fetchDocFiles();
  }, []);

  // Load API docs lazily when tab is selected
  useEffect(() => {
    if (activeTab === 'api' && !systemDocs && !loading) {
      fetchSystemDocs();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocFiles = async () => {
    try {
      setLoadingFiles(true);
      setFilesError(null);
      const files = await apiService.getDocFiles();
      setDocFiles(Array.isArray(files) ? files : []);
      // Auto-select first file
      if (Array.isArray(files) && files.length > 0 && !selectedSlug) {
        selectDoc(files[0].slug);
      }
    } catch (err) {
      setFilesError(err instanceof Error ? err.message : 'Failed to fetch documentation files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const selectDoc = async (slug: string) => {
    setSelectedSlug(slug);
    try {
      setLoadingContent(true);
      const data = await apiService.getDocFileBySlug(slug);
      setDocContent(typeof data === 'string' ? data : data?.content || data?.markdown || '');
    } catch (err) {
      setDocContent(`> Failed to load document: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingContent(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ---- API Docs (existing logic, unchanged) ----
  const fetchSystemDocs = async () => {
    try {
      setLoading(true);
      setError(null);
      const docs = await apiService.getApiDocs();
      setSystemDocs(docs);
      if (docs && typeof docs === 'object') {
        setExpandedSections(new Set(Object.keys(docs)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch system documentation');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (systemDocs && typeof systemDocs === 'object') {
      setExpandedSections(new Set(Object.keys(systemDocs)));
    }
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const getSectionIcon = (section: string) => {
    const lower = section.toLowerCase();
    if (lower.includes('api') || lower.includes('endpoint')) return '🔌';
    if (lower.includes('database') || lower.includes('model') || lower.includes('schema')) return '🗄️';
    if (lower.includes('auth') || lower.includes('security')) return '🔐';
    if (lower.includes('cache') || lower.includes('redis')) return '⚡';
    if (lower.includes('config') || lower.includes('setting')) return '⚙️';
    if (lower.includes('flow') || lower.includes('process')) return '🔄';
    if (lower.includes('service')) return '🔧';
    if (lower.includes('feature') || lower.includes('module')) return '📦';
    if (lower.includes('error') || lower.includes('exception')) return '⚠️';
    if (lower.includes('test')) return '🧪';
    if (lower.includes('deploy') || lower.includes('environment')) return '🚀';
    if (lower.includes('overview') || lower.includes('introduction')) return '📋';
    if (lower.includes('user') || lower.includes('player')) return '👤';
    if (lower.includes('contest') || lower.includes('game')) return '🏆';
    if (lower.includes('series') || lower.includes('match')) return '🏏';
    if (lower.includes('notification') || lower.includes('alert')) return '🔔';
    if (lower.includes('payment') || lower.includes('transaction')) return '💳';
    if (lower.includes('webhook') || lower.includes('event')) return '📡';
    if (lower.includes('log') || lower.includes('audit')) return '📝';
    return '📄';
  };

  const renderDocValue = (value: any, depth: number = 0, path: string = ''): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <span className={`px-2 py-0.5 text-xs rounded font-medium ${value ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }

    if (typeof value === 'number') {
      return <span className="text-blue-600 dark:text-blue-400 font-mono">{value}</span>;
    }

    if (typeof value === 'string') {
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline break-all">
            {value}
          </a>
        );
      }
      if (value.includes('/') || value.includes('.') || value.startsWith(':') || value.includes('_')) {
        return <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-300 text-gray-800 dark:text-gray-200 rounded text-sm font-mono break-all">{value}</code>;
      }
      if (['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(value.toUpperCase())) {
        const methodColors: Record<string, string> = {
          GET: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
          POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
          PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
          DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
          PATCH: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
        };
        return (
          <span className={`px-2 py-0.5 text-xs rounded font-bold ${methodColors[value.toUpperCase()] || ''}`}>
            {value.toUpperCase()}
          </span>
        );
      }
      return <span className="text-gray-700 dark:text-gray-300">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">Empty list</span>;
      }
      if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
        return (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {value.map((item, idx) => (
              <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-gray-300 rounded">
                {String(item)}
              </span>
            ))}
          </div>
        );
      }
      return (
        <div className={`mt-2 space-y-2 ${depth > 0 ? 'ml-4 pl-3 border-l-2 border-gray-200 dark:border-dark-300' : ''}`}>
          {value.map((item, idx) => (
            <div key={idx} className="p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium mb-2 block">Item {idx + 1}</span>
              {renderDocValue(item, depth + 1, `${path}[${idx}]`)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="text-gray-400 italic">Empty object</span>;
      }
      return (
        <div className={`space-y-2 ${depth > 0 ? 'mt-2 ml-4 pl-3 border-l-2 border-gray-200 dark:border-dark-300' : ''}`}>
          {entries.map(([key, val]) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[140px] capitalize shrink-0">
                {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}:
              </span>
              <div className="flex-1">{renderDocValue(val, depth + 1, `${path}.${key}`)}</div>
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  const filteredDocs = systemDocs && typeof systemDocs === 'object'
    ? Object.entries(systemDocs).filter(([key, value]) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        if (key.toLowerCase().includes(searchLower)) return true;
        return JSON.stringify(value).toLowerCase().includes(searchLower);
      })
    : [];

  // ---- Render ----

  const tabItems = [
    { id: 'files' as TabType, label: 'Documentation Files', icon: FaFolder },
    { id: 'api' as TabType, label: 'API Docs', icon: FaCode },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FaBook className="h-6 w-6 text-indigo-500" />
            System Documentation
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            System design and architecture documentation
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-dark-300/40">
          {tabItems.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-all relative ${
                  isActive
                    ? 'text-indigo-700 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{tab.label}</span>
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />}
              </button>
            );
          })}
        </div>

        {/* Documentation Files Tab */}
        {activeTab === 'files' && (
          <div className="flex flex-col lg:flex-row" style={{ minHeight: '60vh' }}>
            {/* File List Sidebar */}
            <div className="lg:w-72 xl:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-dark-300/40 flex-shrink-0">
              <div className="p-3 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Files ({docFiles.length})
                </span>
                <button
                  onClick={fetchDocFiles}
                  disabled={loadingFiles}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Refresh file list"
                >
                  <FaSyncAlt className={`h-3 w-3 ${loadingFiles ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {loadingFiles && docFiles.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <FaSyncAlt className="h-5 w-5 text-indigo-500 animate-spin" />
                </div>
              ) : filesError && docFiles.length === 0 ? (
                <div className="p-4 text-sm text-red-600 dark:text-red-400">{filesError}</div>
              ) : (
                <div className="overflow-y-auto max-h-[50vh] lg:max-h-none">
                  {docFiles.map((file) => (
                    <button
                      key={file.slug}
                      onClick={() => selectDoc(file.slug)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-dark-300/20 transition-colors ${
                        selectedSlug === file.slug
                          ? 'bg-indigo-50 dark:bg-indigo-500/10 border-l-2 border-l-indigo-500'
                          : 'hover:bg-gray-50 dark:hover:bg-dark-300/30 border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FaFileAlt className={`h-3.5 w-3.5 flex-shrink-0 ${
                          selectedSlug === file.slug ? 'text-indigo-500' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium truncate ${
                          selectedSlug === file.slug
                            ? 'text-indigo-700 dark:text-indigo-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {file.slug.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 ml-5.5 text-[10px] text-gray-400 dark:text-gray-500">
                        <span>{file.filename}</span>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Markdown Content Viewer */}
            <div className="flex-1 overflow-auto">
              {!selectedSlug ? (
                <div className="flex items-center justify-center h-full py-20">
                  <div className="text-center">
                    <FaFileAlt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">Select a document to view</p>
                  </div>
                </div>
              ) : loadingContent ? (
                <div className="flex items-center justify-center h-full py-20">
                  <FaSyncAlt className="h-6 w-6 text-indigo-500 animate-spin" />
                </div>
              ) : (
                <div className="p-6 lg:p-8">
                  <div className="markdown-content prose-custom">
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-0 mb-4 pb-2 border-b border-gray-200 dark:border-dark-300/40">
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3 pb-1 border-b border-gray-100 dark:border-dark-300/20">
                            {children}
                          </h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-2">
                            {children}
                          </h3>
                        ),
                        h4: ({ children }) => (
                          <h4 className="text-base font-semibold text-gray-700 dark:text-gray-300 mt-4 mb-2">
                            {children}
                          </h4>
                        ),
                        p: ({ children }) => (
                          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-2">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside text-sm text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-2">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="leading-relaxed">{children}</li>
                        ),
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                            {children}
                          </a>
                        ),
                        code: ({ className, children, ...props }) => {
                          const isBlock = className?.includes('language-');
                          if (isBlock) {
                            return (
                              <code className={`block bg-gray-900 text-green-400 rounded-lg p-4 text-xs overflow-x-auto font-mono mb-4 ${className || ''}`} {...props}>
                                {children}
                              </code>
                            );
                          }
                          return (
                            <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-300 text-gray-800 dark:text-gray-200 rounded text-sm font-mono" {...props}>
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children }) => (
                          <pre className="bg-gray-900 rounded-lg overflow-x-auto mb-4 text-xs">
                            {children}
                          </pre>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 pl-4 pr-3 py-2 mb-4 rounded-r-lg text-sm text-gray-700 dark:text-gray-300">
                            {children}
                          </blockquote>
                        ),
                        table: ({ children }) => (
                          <div className="overflow-x-auto mb-4 rounded-lg border border-gray-200 dark:border-dark-300/40">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40 text-sm">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead className="bg-gray-50 dark:bg-dark-300/30">{children}</thead>
                        ),
                        th: ({ children }) => (
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-dark-300/20">
                            {children}
                          </td>
                        ),
                        hr: () => <hr className="my-6 border-gray-200 dark:border-dark-300/40" />,
                        strong: ({ children }) => (
                          <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-gray-600 dark:text-gray-400">{children}</em>
                        ),
                      }}
                    >
                      {docContent}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* API Docs Tab */}
        {activeTab === 'api' && (
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                  <FaSyncAlt className="h-8 w-8 text-indigo-500 animate-spin" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading API documentation...</p>
                </div>
              </div>
            ) : error && !systemDocs ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <FaTimesCircle className="h-12 w-12 text-red-500 mx-auto" />
                  <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>
                  <button
                    onClick={fetchSystemDocs}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Search and Controls */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search documentation..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchSystemDocs}
                      disabled={loading}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-400 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-300 transition-colors"
                    >
                      <FaSyncAlt className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      onClick={expandAll}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-400 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-300 transition-colors"
                    >
                      <FaExpand className="h-3 w-3" />
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-400 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-300 transition-colors"
                    >
                      <FaCompress className="h-3 w-3" />
                      Collapse All
                    </button>
                    <button
                      onClick={() => setShowRawJson(!showRawJson)}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        showRawJson
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-400 hover:bg-gray-200 dark:hover:bg-dark-300'
                      }`}
                    >
                      <FaCode className="h-3 w-3" />
                      JSON
                    </button>
                  </div>
                </div>

                {searchTerm && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Found {filteredDocs.length} section{filteredDocs.length !== 1 ? 's' : ''} matching "{searchTerm}"
                  </div>
                )}

                {/* Raw JSON View */}
                {showRawJson && systemDocs && (
                  <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                    <div className="px-4 py-2 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-300">Raw JSON</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(systemDocs, null, 2));
                        }}
                        className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        Copy to clipboard
                      </button>
                    </div>
                    <pre className="p-4 text-green-400 text-xs overflow-auto max-h-96">
                      {JSON.stringify(systemDocs, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Documentation Sections */}
                {!showRawJson && (
                  <div className="space-y-4">
                    {filteredDocs.length > 0 ? (
                      filteredDocs.map(([sectionKey, sectionValue]) => (
                        <div
                          key={sectionKey}
                          className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm overflow-hidden"
                        >
                          <button
                            onClick={() => toggleSection(sectionKey)}
                            className="w-full flex items-center justify-between px-5 py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-dark-400/30 dark:to-dark-400/50 hover:from-gray-100 hover:to-gray-150 dark:hover:from-dark-400/50 dark:hover:to-dark-400/70 transition-all text-left"
                          >
                            <div className="flex items-center gap-3">
                              {expandedSections.has(sectionKey) ? (
                                <FaChevronDown className="h-3 w-3 text-gray-500" />
                              ) : (
                                <FaChevronRight className="h-3 w-3 text-gray-500" />
                              )}
                              <span className="text-xl">{getSectionIcon(sectionKey)}</span>
                              <span className="font-semibold text-gray-900 dark:text-white capitalize text-lg">
                                {sectionKey.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                              </span>
                              {Array.isArray(sectionValue) && (
                                <span className="px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full font-medium">
                                  {sectionValue.length} items
                                </span>
                              )}
                              {typeof sectionValue === 'object' && !Array.isArray(sectionValue) && sectionValue !== null && (
                                <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-dark-300 text-gray-600 dark:text-gray-400 rounded-full font-medium">
                                  {Object.keys(sectionValue as object).length} properties
                                </span>
                              )}
                            </div>
                          </button>

                          {expandedSections.has(sectionKey) && (
                            <div className="px-5 py-5 border-t border-gray-100 dark:border-dark-300/30">
                              {renderDocValue(sectionValue, 0, sectionKey)}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40">
                        <FaBook className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                        <p className="text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No sections match your search' : 'No documentation available'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Stats Footer */}
                {systemDocs && (
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg">
                    <div className="flex items-center gap-4">
                      <span>
                        {Object.keys(systemDocs).length} total sections
                      </span>
                      <span>
                        {expandedSections.size} expanded
                      </span>
                    </div>
                    <span className="text-xs font-mono">
                      Last refreshed: {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemDocs;
